/**
 * Mega Menu Fix - ES6 Class Component
 * Prevent body scroll and set correct top position
 */
(() => {
  'use strict';

  class MegaMenuFixComponent {
    constructor() {
      this.scrollPosition = 0;
      this.resizeTimeout = null;
      this.megaMenus = [];
      this.observers = [];
      this.init();
    }

    init() {
      document.addEventListener('DOMContentLoaded', () => {
        this.megaMenus = document.querySelectorAll('.MegaMenu--type1, .MegaMenu--type2, .MegaMenu--type3, .MegaMenu--type4');
        if (this.megaMenus.length === 0) return;

        this.updateMegaMenuPosition();
        this.setupResizeHandler();
        this.setupMegaMenuObservers();
        this.setupAnnouncementObserver();
        this.setupCartObserver();
        this.setupPageOverlayClick();
        this.setupMouseEvents();
        this.setDynamicHeights();
        this.checkMegaMenuState();
      });
    }

    getAnnouncementHeight() {
      const announcementBar = document.querySelector('#shopify-section-announcement .announcement-bar-section');
      if (announcementBar && !announcementBar.classList.contains('announcement-bar-hidden')) {
        const h = parseFloat(window.getComputedStyle(announcementBar).height || '0');
        return isNaN(h) ? 0 : Math.round(h);
      }
      return 0;
    }

    setDynamicHeights() {
      const announcementHeight = this.getAnnouncementHeight();
      document.documentElement.style.setProperty('--announcement-height', `${announcementHeight}px`);

      const header = document.querySelector('.shopify-section--header');
      if (header) {
        const headerHeight = header.getBoundingClientRect().height;
        document.documentElement.style.setProperty('--header-height', `${headerHeight}px`);
      }
    }

    updateMegaMenuPosition() {
      this.setDynamicHeights();
    }

    isCartOpen() {
      const cartDrawer = document.getElementById('sidebar-cart');
      return cartDrawer?.getAttribute('aria-hidden') === 'false';
    }

    getOrCreatePageOverlay() {
      let pageOverlay = document.querySelector('.PageOverlay');
      if (!pageOverlay) {
        pageOverlay = document.createElement('div');
        pageOverlay.className = 'PageOverlay';
        document.body.appendChild(pageOverlay);
      }
      
      const announcementHeight = this.getAnnouncementHeight();
      if (this.isCartOpen()) {
        Object.assign(pageOverlay.style, { top: '0px', height: '100vh', zIndex: '3' });
      } else {
        pageOverlay.style.top = `${announcementHeight}px`;
        pageOverlay.style.height = `calc(100vh - ${announcementHeight}px)`;
        pageOverlay.style.zIndex = '';
      }
      
      return pageOverlay;
    }

    checkMegaMenuState() {
      let isAnyOpen = false;
      this.megaMenus.forEach(megaMenu => {
        if (megaMenu.getAttribute('aria-hidden') === 'false') {
          isAnyOpen = true;
        }
      });
      
      const isDesktop = window.innerWidth >= 1240;
      const isMobileDrawer = document.getElementById('header-megamenu-drawer')?.getAttribute('aria-hidden') === 'false';
      const pageOverlay = this.getOrCreatePageOverlay();
      
      if (isAnyOpen && !isMobileDrawer) {
        const announcementHeight = this.getAnnouncementHeight();
        if (this.isCartOpen()) {
          Object.assign(pageOverlay.style, { top: '0px', height: '100vh', zIndex: '3' });
        } else {
          pageOverlay.style.top = `${announcementHeight}px`;
          pageOverlay.style.height = `calc(100vh - ${announcementHeight}px)`;
          pageOverlay.style.zIndex = '';
        }
        
        document.body.classList.add('mega-menu-open');
        pageOverlay.classList.add('is-visible');
        
        if (!isDesktop) {
          this.scrollPosition = window.pageYOffset || document.documentElement.scrollTop || window.scrollY;
          document.body.style.top = `-${this.scrollPosition}px`;
        }
        
        this.updateMegaMenuPosition();
      } else if (!isAnyOpen) {
        const savedScroll = this.scrollPosition || 0;
        document.body.classList.remove('mega-menu-open');
        document.body.style.top = '';
        pageOverlay.classList.remove('is-visible');
        
        if (!isDesktop && savedScroll > 0) {
          window.scrollTo(0, savedScroll);
          document.documentElement.scrollTop = savedScroll;
        }
      }
    }

    setupResizeHandler() {
      window.addEventListener('resize', () => {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
          this.setDynamicHeights();
          this.updateMegaMenuPosition();
          
          const header = document.querySelector('.shopify-section--header');
          if (header && getComputedStyle(header).position === 'sticky') {
            const announcementHeight = this.getAnnouncementHeight();
            const currentTop = parseFloat(getComputedStyle(header).top) || 0;
            const expectedTop = announcementHeight;
            
            if (Math.abs(currentTop - expectedTop) > 1) {
              header.style.top = `${expectedTop}px`;
              header.style.marginBottom = window.innerWidth > 1239 ? `${expectedTop}px` : '0px';
            }
          }
        }, 50);
      }, { passive: true });
    }

    setupMegaMenuObservers() {
      const observer = new MutationObserver(() => {
        this.checkMegaMenuState();
        this.updateMegaMenuPosition();
      });

      this.megaMenus.forEach(megaMenu => {
        observer.observe(megaMenu, {
          attributes: true,
          attributeFilter: ['aria-hidden']
        });
      });

      this.observers.push(observer);
    }

    setupAnnouncementObserver() {
      const announcementBar = document.querySelector('#shopify-section-announcement .announcement-bar-section');
      if (!announcementBar) return;

      const updateOverlay = () => {
        this.setDynamicHeights();
        this.updateMegaMenuPosition();
        if (this.isCartOpen()) {
          const pageOverlay = this.getOrCreatePageOverlay();
          Object.assign(pageOverlay.style, { top: '0px', height: '100vh', zIndex: '3' });
        }
      };

      const observer = new MutationObserver(updateOverlay);
      observer.observe(announcementBar, { attributes: true, attributeFilter: ['class', 'style'] });
      this.observers.push(observer);

      document.addEventListener('announcement-bar-closed', updateOverlay);
    }

    setupCartObserver() {
      const cartDrawer = document.getElementById('sidebar-cart');
      if (!cartDrawer) return;

      const observer = new MutationObserver(() => {
        const pageOverlay = this.getOrCreatePageOverlay();
        if (this.isCartOpen()) {
          Object.assign(pageOverlay.style, { top: '0px', height: '100vh', zIndex: '4' });
        } else {
          const announcementHeight = this.getAnnouncementHeight();
          pageOverlay.style.top = `${announcementHeight}px`;
          pageOverlay.style.height = `calc(100vh - ${announcementHeight}px)`;
          pageOverlay.style.zIndex = '';
        }
      });

      observer.observe(cartDrawer, {
        attributes: true,
        attributeFilter: ['aria-hidden']
      });

      this.observers.push(observer);
    }

    setupPageOverlayClick() {
      const pageOverlay = this.getOrCreatePageOverlay();
      pageOverlay.addEventListener('click', () => {
        if (window.innerWidth >= 1240) {
          let isAnyOpen = false;
          this.megaMenus.forEach(megaMenu => {
            if (megaMenu.getAttribute('aria-hidden') === 'false') {
              isAnyOpen = true;
              megaMenu.setAttribute('aria-hidden', 'true');
            }
          });
          if (isAnyOpen) {
            this.checkMegaMenuState();
          }
        }
      });
    }

    setupMouseEvents() {
      document.addEventListener('mouseover', (e) => {
        if (e.target.closest('.HorizontalList__Item')) {
          setTimeout(() => {
            this.checkMegaMenuState();
            this.updateMegaMenuPosition();
          }, 50);
        }
      });
    }
  }

  new MegaMenuFixComponent();
})();
